import { Cancel } from "@mui/icons-material";
import { Card, Grid, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Methods } from "api";
import { TYPE_ERROR } from "common/constants/common";
import * as MESSAGES from "common/constants/messages";
import { CLAIM_STATUS } from "common/constants/options";
import type { HeaderActionButtonId } from "common/types/commonTypes";
import { LOADING_STATES } from "common/types/loading";
import AssessmentCard from "components/detailsPage/AssessmentCard";
import ContactCard from "components/detailsPage/ContactCard";
import HeaderCard from "components/detailsPage/HeaderCard";
import HistoryCard from "components/detailsPage/HistoryCard";
import LoadingCircle from "components/loadingCircle";
import dayjs from "dayjs";
import { useAPI, useAuth, useLoading, usePopups } from "hooks";
import { compact } from "lodash";
import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { CcgUpdateClaimInput, Claim, User } from "s12-library/types";
interface PaidDetailsProps {
  goBack: (from: "claimUpdating" | "backButton" | "sideMenuClick") => void;
  rowId: string;
}

const PaidDetails: FC<PaidDetailsProps> = ({ goBack, rowId }) => {
  const { handleConfirmation, handleBannerMessage } = usePopups();
  const [data, setData] = useState<Claim | undefined>(undefined);
  const { user } = useAuth();

  const { loadingState, setLoadingState, finishLoading } = useLoading();

  const navigate = useNavigate();
  const location = useLocation();

  // Mutations
  const {
    loading: updateClaimLoading,
    error: updateClaimError,
    trigger: updateClaim,
  } = useAPI<
    Claim[],
    {
      input: CcgUpdateClaimInput;
    }
  >({
    method: Methods.PUT,
    fieldName: "updateClaim",
  });

  // Queries
  const {
    data: claimUserData,
    error: claimUserDataError,
    trigger: getUserData,
  } = useAPI<
    User,
    {
      id: string;
    }
  >({
    method: Methods.GET,
    fieldName: "getClaimUser",
    manual: true,
  });

  const {
    data: paidClaimDetailsData,
    loading: paidClaimDetailsLoading,
    error: paidClaimDetailsError,
  } = useAPI<
    Claim,
    {
      id: string;
    }
  >({
    method: Methods.GET,
    fieldName: "getClaim",
    args: {
      id: rowId,
    },
  });

  useEffect(() => {
    if (user) {
      getUserData({
        id: user.username,
      });
    }
  }, [getUserData, user]);

  const userPermissions = useMemo(
    () => ({
      claimApprover: claimUserData?.claimApprover ?? false,
      claimPayer: claimUserData?.claimPayer ?? false,
    }),
    [claimUserData],
  );

  const buttonClick = useCallback(
    (name: HeaderActionButtonId) => {
      if (!user) {
        handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
        return;
      }
      switch (name) {
        case "MarkAsUnpaid":
          !!userPermissions.claimPayer &&
            handleConfirmation(
              MESSAGES.UNPAID_CLAIM,
              () => {
                setLoadingState(LOADING_STATES.UNDO_PAYMENT);
                updateClaim({
                  input: {
                    id: [rowId],
                    notes: `Claim marked as unpaid by team member@${user.attributes.name ?? "N/A"}@${dayjs().format()}`,
                    status: CLAIM_STATUS.approved,
                  },
                })
                  .then((data) => {
                    if (data && data.length > 0) {
                      handleBannerMessage("success", `Claim marked as unpaid by ${user.attributes.name}`);
                      goBack("claimUpdating");
                    } else {
                      handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
                    }
                  })
                  .catch((error) => {
                    handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
                    Sentry.captureException("Claims > paid, mark as unpaid error.", error);
                  })
                  .finally(finishLoading);
              },
              "Mark as Unpaid",
              "MARK AS UNPAID",
              "CANCEL",
            );
          break;
        default:
          break;
      }
    },
    [
      finishLoading,
      goBack,
      handleBannerMessage,
      handleConfirmation,
      rowId,
      setLoadingState,
      updateClaim,
      user,
      userPermissions.claimPayer,
    ],
  );

  useEffect(() => {
    if (paidClaimDetailsData && paidClaimDetailsData.status === "approved_and_paid") {
      setData(paidClaimDetailsData);
    }
  }, [paidClaimDetailsData]);

  useEffect(() => {
    if (paidClaimDetailsError || updateClaimError || claimUserDataError) {
      handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
    }
  }, [claimUserDataError, handleBannerMessage, paidClaimDetailsError, updateClaimError]);

  useEffect(() => {
    if (location?.state?.isSameMenu && !!goBack) {
      goBack("sideMenuClick");
      navigate(location.pathname, {
        state: { isSameMenu: false, isInDetailsPage: false },
      });
    }
  }, [goBack, location.pathname, location?.state?.isSameMenu, navigate]);

  return (
    <>
      {paidClaimDetailsLoading ? (
        <LoadingCircle />
      ) : data ? (
        <>
          <HeaderCard
            status={data?.status === CLAIM_STATUS.paid ? "PAID" : ""}
            assigneeName={data?.assigneeName ?? ""}
            endpointsLoading={loadingState}
            goBack={() => goBack("backButton")}
            buttons={compact([
              userPermissions.claimPayer
                ? {
                    key: "MarkAsUnpaid",
                    loading: updateClaimLoading,
                    icon: <Cancel />,
                  }
                : null,
            ])}
            onClick={(a) => {
              buttonClick(a);
            }}
            claimId={data?.displayId}
          />
          <AssessmentCard claim={data} />
          <ContactCard docData={data?.doctor} amhpData={data?.amhp} />
          <HistoryCard data={data} />
        </>
      ) : (
        <Grid
          m={1}
          p={2}
          component={Card}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            borderRadius: 1,
            minHeight: window.innerHeight - 85,
          }}>
          <Grid container m={0}>
            <Grid container spacing={3} sx={{ height: "fit-content" }}>
              <Grid item xs={12}>
                <Typography variant="h1">Unexpected error occurred when fetching the claim data</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default memo(PaidDetails);
